import { getBackendOrigin } from './apiBridge';
import { getStoredToken } from './authService';

const BASE = `${getBackendOrigin()}/api/voice`;

function withVoiceAuth(init: RequestInit = {}): RequestInit {
  const headers = new Headers(init.headers);
  const token = getStoredToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return { ...init, credentials: 'include', headers };
}

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 800;

/** Retry fetch on network errors / 5xx (transient failures when backend is starting up) */
async function voiceFetch(path: string, init: RequestInit = {}, retries = MAX_RETRIES): Promise<Response> {
  let lastError: Error | undefined;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(`${BASE}${path}`, withVoiceAuth(init));
      if (res.ok || res.status < 500) return res; // 4xx → don't retry
      // 5xx → may be transient
      lastError = new Error(`HTTP ${res.status}`);
      if (attempt < retries) await new Promise(r => setTimeout(r, RETRY_DELAY_MS * (attempt + 1)));
    } catch (err: any) {
      // Network error (ECONNREFUSED, etc.) → retry
      lastError = err;
      if (attempt < retries) await new Promise(r => setTimeout(r, RETRY_DELAY_MS * (attempt + 1)));
    }
  }
  throw lastError || new Error('Voice service unreachable');
}

async function readError(res: Response, fallback: string): Promise<string> {
  const data = await res.json().catch(() => null);
  return data?.error || fallback;
}

export async function uploadSamples(files: File[]): Promise<{ urls: string[]; filenames: string[]; count: number }> {
  const form = new FormData();
  files.forEach(f => form.append('samples', f));

  console.log('[VoiceService] Uploading', files.length, 'samples, sizes:', files.map(f => f.size));
  const url = `${BASE}/samples`;
  console.log('[VoiceService] POST', url);
  const res = await voiceFetch('/samples', { method: 'POST', body: form });
  console.log('[VoiceService] Upload response:', res.status, res.statusText);
  if (!res.ok) {
    throw new Error(await readError(res, 'Upload failed'));
  }
  const data = await res.json();
  console.log('[VoiceService] Upload result:', data);
  return data;
}

export async function cloneVoice(sampleUrls: string[], name: string, provider?: string): Promise<{ voiceId: string; name: string; provider: string }> {
  console.log('[VoiceService] Cloning voice with name:', name, 'urls:', sampleUrls);
  const res = await voiceFetch('/clone', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sampleUrls, name, provider: provider || 'cosyvoice' }),
  });
  console.log('[VoiceService] Clone response:', res.status, res.statusText);
  if (!res.ok) {
    throw new Error(await readError(res, 'Clone failed'));
  }
  const data = await res.json();
  console.log('[VoiceService] Clone result:', data);
  return data;
}

export async function listVoices(): Promise<{ cloned: any[]; premade: any[] }> {
  const res = await voiceFetch('/voices');
  if (!res.ok) throw new Error(await readError(res, 'Failed to fetch voices'));
  return res.json();
}

export async function deleteVoice(voiceId: string): Promise<void> {
  const res = await voiceFetch(`/${voiceId}`, { method: 'DELETE' });
  if (!res.ok) throw new Error(await readError(res, 'Failed to delete voice'));
}

export async function synthesizeSpeech(text: string, voiceId: string, provider?: string, userId?: string): Promise<ArrayBuffer> {
  const res = await voiceFetch('/synthesize', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, voiceId, provider, userId }),
  });
  if (!res.ok) throw new Error(await readError(res, 'Speech synthesis failed'));
  return res.arrayBuffer();
}
